import {
    FlatTree,
    FlatTreeItem,
    mergeClasses,
    TreeItemLayout,
    treeItemLevelToken,
    TreeItemOpenChangeData,
    TreeItemOpenChangeEvent,
    TreeItemValue,
} from '@fluentui/react-components';
import { ChevronRight12Regular } from '@fluentui/react-icons';
import * as React from 'react';
import { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { IUnitTestProps } from '../../data-types/IUnitTestProps';
import { useTraceCalculationStyles } from '../../hooks/useTraceCalculationStyles';
import { DefaultTooltipHostV9 } from '../common/DefaultTooltipHostV9';
import { IWhitePaperRowTrace } from './ReportDrillDownUtils';

export interface ITraceCalculationTabPanelProps extends IUnitTestProps {
    definingRows: IWhitePaperRowTrace[];
    totalRow: IWhitePaperRowTrace;
    rowsImpacted: IWhitePaperRowTrace[];
    onOpenChange?: (event: TreeItemOpenChangeEvent, data: TreeItemOpenChangeData) => void;
    expandedChildren?: Map<string, IWhitePaperRowTrace[]>;
}

interface ILazyTraceItemProps {
    trace: IWhitePaperRowTrace;
    onOpenChange?: (event: TreeItemOpenChangeEvent, data: TreeItemOpenChangeData) => void;
    expandedChildren?: Map<string, IWhitePaperRowTrace[]>;
    level: number;
    setSize: number;
    posInSet: number;
    parentValue?: TreeItemValue;
}

const LazyTraceItem = React.forwardRef<HTMLDivElement, ILazyTraceItemProps>(
    ({ trace, onOpenChange, expandedChildren, level, setSize, posInSet, parentValue }, ref) => {
        const styles = useTraceCalculationStyles();
        const hasSubTraces = trace.hasChildren ?? false;
        const isOpen = hasSubTraces && expandedChildren?.has(trace.uiKey!);
        const childrenFromMap = trace.uiKey ? expandedChildren?.get(trace.uiKey) : undefined;
        const children = childrenFromMap ?? [];

        const handleOpenChange = useCallback(
            (e: TreeItemOpenChangeEvent, data: TreeItemOpenChangeData) => {
                onOpenChange?.(e, data);
            },
            [onOpenChange]
        );

        return (
            <>
                <FlatTreeItem
                    key={trace.uiKey}
                    ref={ref}
                    value={trace.uiKey!}
                    style={{ [treeItemLevelToken]: level }}
                    aria-level={level}
                    aria-setsize={setSize}
                    aria-posinset={posInSet}
                    itemType={hasSubTraces ? 'branch' : 'leaf'}
                    open={isOpen}
                    onOpenChange={hasSubTraces ? handleOpenChange : undefined}
                    parentValue={parentValue}
                    className={styles.defaultRowStyle}
                >
                    <TreeItemLayout
                        className={styles.treeItemLayoutMain}
                        main={{ className: mergeClasses(styles.flexFullWidth, styles.treeItemLayoutMain) }}
                        expandIcon={null}
                    >
                        <div className={styles.rowData}>
                            <div className={styles.rowColumn}>
                                <div
                                    className={
                                        hasSubTraces
                                            ? styles.rowNumberWrapper
                                            : mergeClasses(styles.rowNumberWrapper, styles.leafRowNumberWrapper)
                                    }
                                >
                                    {hasSubTraces && (
                                        <ChevronRight12Regular
                                            className={mergeClasses(styles.chevronIcon, isOpen && styles.chevronIconOpen)}
                                        />
                                    )}
                                    <span className={styles.rowNumberText}>
                                        <DefaultTooltipHostV9
                                            appearance='inverted'
                                            withArrow
                                            positioning='after'
                                            content={trace.rowNumber?.toString() ?? ''}
                                        >
                                            {trace.rowNumber}
                                        </DefaultTooltipHostV9>
                                    </span>
                                </div>
                            </div>
                            <div className={styles.descriptionColumn}>
                                <DefaultTooltipHostV9
                                    appearance='inverted'
                                    withArrow
                                    positioning='before'
                                    content={trace.description ?? ''}
                                >
                                    {trace.description}
                                </DefaultTooltipHostV9>
                            </div>
                            <div className={styles.definitionColumn}>
                                <DefaultTooltipHostV9 appearance='inverted' withArrow positioning='before' content={trace.definition ?? ''}>
                                    {trace.definition}
                                </DefaultTooltipHostV9>
                            </div>
                            <div className={styles.amountColumn}>
                                <DefaultTooltipHostV9 appearance='inverted' withArrow positioning='before' content={trace.amount ?? ''}>
                                    {trace.amount}
                                </DefaultTooltipHostV9>
                            </div>
                        </div>
                    </TreeItemLayout>
                </FlatTreeItem>
                {hasSubTraces &&
                    isOpen &&
                    children.map((subTrace, index) => (
                        <LazyTraceItem
                            key={subTrace.uiKey}
                            trace={subTrace}
                            onOpenChange={onOpenChange}
                            expandedChildren={expandedChildren}
                            level={level + 1}
                            setSize={children.length}
                            posInSet={index + 1}
                            parentValue={trace.uiKey!}
                        />
                    ))}
            </>
        );
    }
);

const TraceCalculationTabPanel: FC<ITraceCalculationTabPanelProps> = (props: ITraceCalculationTabPanelProps) => {
    const { t } = useTranslation();
    const styles = useTraceCalculationStyles();

    return (
        <div
            id='traceCalculationGrid'
            data-testid='traceCalculationGrid'
            className={mergeClasses(styles.traceCalculationGrid, styles.customTokens)}
        >
            <div className={styles.columnHeader}>
                <div className={styles.rowColumnHeader}>{t('row')}</div>
                <div className={styles.descriptionColumnHeader}>{t('description')}</div>
                <div className={styles.definitionColumnHeader}>{t('definition')}</div>
                <div className={styles.amountColumnHeader}>{t('amount')}</div>
            </div>

            <FlatTree aria-label='Trace Calculation' size='small' className={styles.treeRoot}>
                <FlatTreeItem
                    key='total-section'
                    value='total-section'
                    aria-level={1}
                    aria-setsize={3}
                    aria-posinset={1}
                    itemType='leaf'
                    className={mergeClasses(styles.border, styles.totalSectionHeader)}
                >
                    <TreeItemLayout
                        className={mergeClasses(styles.totalRowLayout)}
                        main={{ className: mergeClasses(styles.flexFullWidth, styles.totalSectionLayout) }}
                    >
                        <strong className={styles.totalHeaderText}>{t('total').toString()}</strong>
                    </TreeItemLayout>
                    <FlatTreeItem
                        key={props.totalRow.uiKey}
                        value={props.totalRow.uiKey!}
                        aria-level={2}
                        aria-setsize={1}
                        aria-posinset={1}
                        itemType='leaf'
                        parentValue='total-section'
                        className={mergeClasses(styles.totalRowItem, styles.totalRowLayout)}
                    >
                        <TreeItemLayout
                            className={mergeClasses(styles.totalRowLayout)}
                            main={{
                                className: mergeClasses(styles.flexFullWidth, styles.treeItemLayoutMain),
                            }}
                        >
                            <div className={styles.rowData}>
                                <div className={styles.rowColumn}>
                                    <div className={styles.totalrowNumberWrapper}>
                                        <span className={styles.rowNumberText}>{props.totalRow.rowNumber}</span>
                                    </div>
                                </div>
                                <div className={styles.descriptionColumn}>
                                    <DefaultTooltipHostV9
                                        appearance='inverted'
                                        positioning='before'
                                        withArrow
                                        content={props.totalRow.description ?? ''}
                                    >
                                        {props.totalRow.description}
                                    </DefaultTooltipHostV9>
                                </div>
                                <div className={styles.definitionColumn}>
                                    <DefaultTooltipHostV9
                                        appearance='inverted'
                                        positioning='before'
                                        withArrow
                                        content={props.totalRow.definition ?? ''}
                                    >
                                        {props.totalRow.definition}
                                    </DefaultTooltipHostV9>
                                </div>
                                <div className={styles.amountColumn}>
                                    <DefaultTooltipHostV9
                                        appearance='inverted'
                                        positioning='before'
                                        withArrow
                                        content={props.totalRow.amount ?? ''}
                                    >
                                        {props.totalRow.amount}
                                    </DefaultTooltipHostV9>
                                </div>
                            </div>
                        </TreeItemLayout>
                    </FlatTreeItem>
                </FlatTreeItem>

                <FlatTreeItem
                    key='defining-rows-section'
                    value='definingRows'
                    aria-label={t('definingRows').toString()}
                    aria-level={1}
                    aria-setsize={3}
                    aria-posinset={2}
                    itemType='branch'
                    className={mergeClasses(styles.border, styles.definingSectionHeader)}
                    open={props.expandedChildren?.has('definingRows') ?? false}
                    onOpenChange={props.onOpenChange}
                >
                    <TreeItemLayout className={styles.groupHeader} main={{ className: mergeClasses(styles.flexFullWidth) }}>
                        <strong>{t('definingRows').toString()}</strong>
                    </TreeItemLayout>
                    {props.expandedChildren?.has('definingRows') &&
                        (props.definingRows && props.definingRows.length > 0 ? (
                            props.definingRows.map((trace, index) => (
                                <div key={trace.uiKey} onClick={(e) => e.stopPropagation()}>
                                    <LazyTraceItem
                                        trace={trace}
                                        onOpenChange={props.onOpenChange}
                                        expandedChildren={props.expandedChildren}
                                        level={2}
                                        setSize={props.definingRows.length}
                                        posInSet={index + 1}
                                        parentValue='definingRows'
                                    />
                                </div>
                            ))
                        ) : (
                            <FlatTreeItem
                                key={'definingRows-empty'}
                                value={'definingRows-empty'}
                                aria-level={2}
                                aria-setsize={1}
                                aria-posinset={1}
                                itemType='leaf'
                                parentValue='definingRows'
                                className={styles.defaultRowStyle}
                            >
                                <TreeItemLayout
                                    className={mergeClasses(styles.groupHeader, styles.treeItemLayoutMain)}
                                    main={{ className: mergeClasses(styles.flexFullWidth, styles.treeItemLayoutMain) }}
                                >
                                    <div onClick={(e) => e.stopPropagation()} className={styles.emptyRowPadding}>
                                        <span>{t('noRecordsToDisplay')}</span>
                                    </div>
                                </TreeItemLayout>
                            </FlatTreeItem>
                        ))}
                </FlatTreeItem>

                <FlatTreeItem
                    key='rows-impacted-section'
                    value='rowsImpacted'
                    aria-label={t('rowsImpacted').toString()}
                    aria-level={1}
                    aria-setsize={3}
                    aria-posinset={3}
                    itemType='branch'
                    className={mergeClasses(styles.border, styles.impactedSectionHeader)}
                    open={props.expandedChildren?.has('rowsImpacted') ?? false}
                    onOpenChange={props.onOpenChange}
                >
                    <TreeItemLayout className={styles.groupHeader} main={{ className: mergeClasses(styles.flexFullWidth) }}>
                        <strong>{t('rowsImpacted').toString()}</strong>
                    </TreeItemLayout>
                    {props.expandedChildren?.has('rowsImpacted') &&
                        props.rowsImpacted &&
                        props.rowsImpacted.length > 0 &&
                        props.rowsImpacted.map((trace, index) => (
                            <div key={trace.uiKey} onClick={(e) => e.stopPropagation()}>
                                <LazyTraceItem
                                    trace={trace}
                                    onOpenChange={props.onOpenChange}
                                    expandedChildren={props.expandedChildren}
                                    level={2}
                                    setSize={props.rowsImpacted.length}
                                    posInSet={index + 1}
                                    parentValue={'rowsImpacted'}
                                />
                            </div>
                        ))}
                    {props.expandedChildren?.has('rowsImpacted') && (!props.rowsImpacted || props.rowsImpacted.length === 0) && (
                        <FlatTreeItem
                            key={'rowsImpacted-empty'}
                            value={'rowsImpacted-empty'}
                            aria-level={2}
                            aria-setsize={1}
                            aria-posinset={1}
                            itemType='leaf'
                            parentValue='rowsImpacted'
                            className={styles.defaultRowStyle}
                        >
                            <TreeItemLayout
                                className={mergeClasses(styles.groupHeader, styles.treeItemLayoutMain)}
                                main={{ className: mergeClasses(styles.flexFullWidth, styles.treeItemLayoutMain) }}
                            >
                                <div onClick={(e) => e.stopPropagation()} className={styles.emptyRowPadding}>
                                    <span>{t('noRecordsToDisplay')}</span>
                                </div>
                            </TreeItemLayout>
                        </FlatTreeItem>
                    )}
                </FlatTreeItem>
            </FlatTree>
        </div>
    );
};

export default TraceCalculationTabPanel;
